import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { EnvironmentService } from '@app/core/services/environment.service';
import { PendingTimeOffRecord } from '../models/pending-time-off-report.model';

const headers = new HttpHeaders({
    'Content-Type': 'application/json',
});

@Injectable({
    providedIn: 'root'
})
export class PendingTimeOffReportService {

    constructor(
        private http: HttpClient,
        private translate: TranslateService,
        private overlayService: OverlayService,
        private datePipe: DatePipe,
        private envService: EnvironmentService
    ) {
    }

    public fetch(state: State, filterString: string, sortString: string, showOverlay?: boolean,
                 filterInactive?: boolean): Observable<GridDataResult> {
        let params = new HttpParams();
        state.skip ? params = params.append('skip', state.skip.toString()) : null;
        state.take ? params = params.append('take', state.take.toString()) : null;

        // Filter for pending requests
        const pendingFilter = '(RequestStatus.Name like "Pending")';
        if (filterString) {
            filterString = `${pendingFilter} and (${filterString})`;
        } else {
            filterString = pendingFilter;
        }

        filterString ? params = params.append('Filter', filterString) : null;
        // sortString ? params = params.append('Sort', sortString) : null;
        // filterInactive ? params = params.append('FilterInactive', filterInactive.toString()) : false;

        if (showOverlay) {
            this.overlayService.show(this.translate.instant('FetchingData'));
        }

        return this.http.get(
            `${this.envService.env.apiUrl}Workflows/Absences`,
            {
                headers,
                params,
            })
            .pipe(
                finalize(() => showOverlay ? this.overlayService.hide() : null),
                map(
                    (response) => {
                        const pendingTimeOffRecords = response['data'].map(item => {
                            const managerFullName = item.managers && item.managers.length > 0
                                ? `${item.managers[0].firstName} ${item.managers[0].lastName}`
                                : '';

                            // Calculate hours if not provided
                            let hours = item.hours;
                            if (!hours && item.from && item.to) {
                                const fromDate = new Date(item.from);
                                const toDate = new Date(item.to);
                                // Calculate business days between (excluding weekends)
                                const dayDiff = this.getBusinessDayCount(fromDate, toDate);
                                // Assume 8-hour workday
                                hours = dayDiff * 8;
                            }

                            return <PendingTimeOffRecord>{
                                id: item.id,
                                employeeFullName: `${item.requestedBy.firstName} ${item.requestedBy.lastName}`,
                                managerFullName: managerFullName,
                                timeOffRequestStartDate: this.datePipe.transform(item.from, 'MM/dd/yyyy'),
                                timeOffRequestEndDate: this.datePipe.transform(item.to, 'MM/dd/yyyy'),
                                hoursInTimeOffRequest: hours || 0,
                                dateSubmittedOn: this.datePipe.transform(item.requestedOn, 'MM/dd/yyyy'),
                                employeeStatus: item.requestedBy.status.name,
                                requestStatus: item.requestStatus?.name || '',
                                timeOffType: item.timeOffType?.name || ''
                            };
                        });

                        return <GridDataResult> {
                            data: pendingTimeOffRecords,
                            total: parseInt(response['total'], 10),
                        };
                    }
                )
            );
    }

    public async queryAll(st?: State, filterString?: string, sortString?: string, filterInactive?: boolean):
        Promise<GridDataResult> {
        const state = Object.assign({}, st);
        state.skip = 0;
        state.take = 100;

        let dataToReturn: GridDataResult = {
            data: [],
            total: null
        };

        while(dataToReturn.total === null || state.skip < dataToReturn.total) {
            const res = await this.fetch(state, filterString, sortString, true, filterInactive).toPromise();

            if (res && res.data && res.data.length > 0) {
                dataToReturn.data = dataToReturn.data.concat(res.data);
                state.skip += state.take;
                dataToReturn.total = res.total;
            } else {
                break;
            }
        }

        return dataToReturn;
    }

    // Helper method to calculate business days between two dates
    private getBusinessDayCount(startDate: Date, endDate: Date): number {
        let count = 0;
        const curDate = new Date(startDate.getTime());
        while (curDate <= endDate) {
            const dayOfWeek = curDate.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                count++;
            }
            curDate.setDate(curDate.getDate() + 1);
        }
        return count;
    }
}
